import { useEffect, useRef, useState, type FormEvent } from 'react';
import { TRUSTED_DEVICE_DAYS } from '@seller/shared';
import { api, ApiError } from '../api/client.js';
import { useRefreshSession } from '../auth/useSession.js';
import { Alert, Button, Card, Field, Input, Spinner } from '../components/ui.js';
import { RecoveryCodes } from '../components/RecoveryCodes.js';

/**
 * The second factor: either enrolling an authenticator or entering a code.
 *
 * Enrollment is two steps on the server — the secret is stored pending until a
 * live code confirms it — so a QR that was never actually scanned cannot leave
 * an account with two-factor half enabled and its owner locked out.
 */

interface EnrollmentResponse {
  otpauthUri: string;
  qrDataUrl: string;
}

interface ConfirmResponse {
  recoveryCodes: string[];
}

export function TwoFactorPage({ mode }: { mode: 'enroll' | 'verify' }) {
  return mode === 'enroll' ? <EnrollView /> : <VerifyView />;
}

// ---------------------------------------------------------------------------
// Entering a code from an already-enrolled authenticator
// ---------------------------------------------------------------------------

function VerifyView() {
  const refreshSession = useRefreshSession();
  const [code, setCode] = useState('');
  // Off by default: the person on a shared or borrowed machine is the one
  // who pays for a wrong default, and they are the least likely to notice it.
  const [rememberDevice, setRememberDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await api.post('/api/auth/mfa', { code, rememberDevice });
      await refreshSession();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That code was not accepted.');
      setBusy(false);
      setCode('');
    }
  }

  return (
    <Shell title="Verification code" subtitle="Enter the current code from your authenticator.">
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field
          label="Code"
          hint="You can also enter one of your recovery codes if you cannot reach your authenticator."
        >
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            // Not type=number: recovery codes are alphanumeric, and a spinner
            // on a one-time code is nothing but a nuisance.
            inputMode="text"
            autoComplete="one-time-code"
            placeholder="123456"
            required
            autoFocus
            disabled={busy}
            className="tabular tracking-widest"
          />
        </Field>

        <label className="flex items-start gap-2 text-sm text-body">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
            disabled={busy}
            className="mt-0.5 h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-brand-600"
          />
          <span>
            Remember this device for {TRUSTED_DEVICE_DAYS} days
            <span className="block text-xs text-muted">
              You will still need your password, but not a code, on this browser. Leave this off on
              a shared computer.
            </span>
          </span>
        </label>

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Verifying…' : 'Verify'}
        </Button>
      </form>

      <SignOutLink />
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// First-time enrollment
// ---------------------------------------------------------------------------

function EnrollView() {
  const refreshSession = useRefreshSession();
  const [enrollment, setEnrollment] = useState<EnrollmentResponse | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);

  // The one setup request this mount makes. Every call to setup mints a new
  // secret and overwrites the pending one, so the page must ask exactly once:
  // in development, StrictMode runs the effect below twice, and two requests in
  // flight together would show whichever answered second while the row kept
  // whichever wrote last — and the code read off the screen would be refused.
  // The ref survives StrictMode's simulated remount, so the second run of the
  // effect attaches to the request the first run already sent.
  const setupRequest = useRef<Promise<EnrollmentResponse> | null>(null);

  useEffect(() => {
    let canceled = false;

    setupRequest.current ??= api.post<EnrollmentResponse>('/api/auth/totp/setup');
    setupRequest.current
      .then((result) => {
        if (!canceled) setEnrollment(result);
      })
      .catch((caught: unknown) => {
        if (!canceled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not start enrollment.');
        }
      });

    return () => {
      canceled = true;
    };
  }, []);

  async function handleConfirm(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const result = await api.post<ConfirmResponse>('/api/auth/totp/confirm', { code });
      // Shown once and never retrievable, so the session is not refreshed until
      // the user has acknowledged them.
      setRecoveryCodes(result.recoveryCodes);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That code was not accepted.');
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  if (recoveryCodes) {
    return <RecoveryCodesView codes={recoveryCodes} onDone={() => void refreshSession()} />;
  }

  return (
    <Shell
      title="Set up two-factor authentication"
      subtitle="Scan this with your authenticator app, then enter the code it shows."
    >
      {error && (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      )}

      {!enrollment ? (
        <div className="py-8">
          <Spinner label="Preparing your authenticator key" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex justify-center rounded-lg bg-surface p-4 ring-1 ring-line">
            <img
              src={enrollment.qrDataUrl}
              alt="Two-factor authentication QR code"
              width={200}
              height={200}
            />
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="text-xs text-muted underline underline-offset-2 hover:text-body"
            >
              {showKey ? 'Hide setup key' : 'Cannot scan? Enter the key manually'}
            </button>

            {showKey && (
              <p className="mt-2 break-all rounded bg-sunken p-2 text-left font-mono text-xs text-muted">
                {enrollment.otpauthUri}
              </p>
            )}
          </div>

          <form onSubmit={(e) => void handleConfirm(e)} className="space-y-4">
            <Field label="Code from your authenticator">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                required
                autoFocus
                disabled={busy}
                className="tabular tracking-widest"
              />
            </Field>

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? 'Confirming…' : 'Confirm and continue'}
            </Button>
          </form>
        </div>
      )}

      <SignOutLink />
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Recovery codes, shown exactly once
// ---------------------------------------------------------------------------

function RecoveryCodesView({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Shell
      title="Save your recovery codes"
      subtitle="These are the only way back in if you lose your authenticator."
    >
      <div className="space-y-5">
        <Alert tone="warning">
          This is the only time these will be shown. Store them somewhere safe — a password manager
          is ideal. Each one works once.
        </Alert>

        <RecoveryCodes codes={codes} filename="recovery-codes.txt" />

        <label className="flex items-start gap-2 text-sm text-body">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-brand-600"
          />
          I have saved these codes somewhere safe.
        </label>

        <Button className="w-full" disabled={!acknowledged} onClick={onDone}>
          Continue
        </Button>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------

function Shell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-4 py-12">
      <div className="w-full max-w-md">
        <header className="mb-6 text-center">
          <h1 className="text-lg font-semibold tracking-tight text-strong">{title}</h1>
          <p className="mt-1 text-sm text-muted">{subtitle}</p>
        </header>
        <Card className="p-6">{children}</Card>
      </div>
    </div>
  );
}

function SignOutLink() {
  const refreshSession = useRefreshSession();

  return (
    <div className="mt-4 text-center">
      <button
        type="button"
        className="text-xs text-muted underline underline-offset-2 hover:text-body"
        onClick={() => {
          void api.post('/api/auth/logout').then(() => refreshSession());
        }}
      >
        Sign in as someone else
      </button>
    </div>
  );
}
